import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Download, Refresh, Upload } from "@mui/icons-material";
import AssignmentImportPreview from "pages/users/components/AssignmentImportPreview.jsx";

const TemplateDownloadBody = ({
  template_options,
  on_template_options_change,
  template_error,
  is_downloading,
  on_download_template,
  is_sys_admin,
  selected_customer,
}) => {
  const handle_option_change = (key, value) => {
    on_template_options_change({ ...template_options, [key]: value });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Vorlage herunterladen
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Laden Sie eine Excel-Vorlage herunter, um Benutzer-Zuordnungen zu
        bearbeiten.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Optionen
        </Typography>

        <FormControl component="fieldset" sx={{ mb: 1 }}>
          <FormLabel component="legend">Vorlagen-Format</FormLabel>
          <RadioGroup
            row
            value={template_options.format}
            onChange={(event) =>
              handle_option_change("format", event.target.value)
            }
          >
            <FormControlLabel
              value="xlsx"
              control={<Radio />}
              label="XLSX (2 Arbeitsblätter)"
            />
            <FormControlLabel
              value="csv"
              control={<Radio />}
              label="CSV (einfache Tabelle)"
            />
          </RadioGroup>
        </FormControl>

        {template_options.format === "csv" && (
          <Alert severity="info" sx={{ mb: 1 }}>
            Für die Spalte <strong>zuordnungsumfang</strong> sind die Werte
            <strong> "alle"</strong> (alle Objekte des Vertrags) und
            <strong> "objektauswahl"</strong> (nur ausgewählte Objekte)
            zulässig.
          </Alert>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={template_options.prepopulated}
              onChange={(event) =>
                handle_option_change("prepopulated", event.target.checked)
              }
            />
          }
          label="Vorlage mit aktuellen Zuordnungen vorausfüllen"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={template_options.include_catalogs}
              onChange={(event) =>
                handle_option_change("include_catalogs", event.target.checked)
              }
              disabled={template_options.format === "csv"}
            />
          }
          label="Katalog-Arbeitsblätter einschließen (Verträge, Objekte, Benutzer)"
        />

        {template_options.format === "csv" && (
          <Typography variant="caption" color="text.secondary">
            Katalog-Arbeitsblätter sind nur in der XLSX-Vorlage verfügbar.
          </Typography>
        )}
      </Box>

      {template_error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {template_error}
        </Alert>
      )}

      <Button
        variant="contained"
        startIcon={
          is_downloading ? <CircularProgress size={20} /> : <Download />
        }
        onClick={on_download_template}
        disabled={is_downloading || (is_sys_admin && !selected_customer)}
        size="large"
      >
        {is_downloading ? "Wird heruntergeladen..." : "Vorlage herunterladen"}
      </Button>
    </Box>
  );
};

export const TemplateDownloadCard = ({ layout = "card", ...props }) => {
  const body = <TemplateDownloadBody {...props} />;

  if (layout === "card") {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>{body}</CardContent>
      </Card>
    );
  }

  return <Box sx={{ mb: 3 }}>{body}</Box>;
};

const CatalogDownloadBody = ({
  catalog_options,
  on_catalog_options_change,
  on_download_catalogs,
  is_downloading,
  catalogs_error,
  is_sys_admin,
  selected_customer,
}) => {
  const handle_option_change = (key, value) => {
    on_catalog_options_change({ ...catalog_options, [key]: value });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Kataloge herunterladen
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Laden Sie vollständige Listen mit IDs herunter, um das Ausfüllen der
        Importvorlagen zu erleichtern.
      </Typography>

      <Box sx={{ mb: 2 }}>
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend">Format</FormLabel>
          <RadioGroup
            row
            value={catalog_options.format}
            onChange={(event) =>
              handle_option_change("format", event.target.value)
            }
          >
            <FormControlLabel
              value="xlsx"
              control={<Radio />}
              label="XLSX (Arbeitsmappe)"
            />
            <FormControlLabel
              value="zip-csv"
              control={<Radio />}
              label="ZIP (CSV-Dateien)"
            />
          </RadioGroup>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              checked={catalog_options.include_users}
              onChange={(event) =>
                handle_option_change("include_users", event.target.checked)
              }
            />
          }
          label="Benutzer-Katalog einschließen"
        />
      </Box>

      {catalogs_error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {catalogs_error}
        </Alert>
      )}

      <Button
        variant="contained"
        startIcon={
          is_downloading ? <CircularProgress size={20} /> : <Download />
        }
        onClick={on_download_catalogs}
        disabled={is_downloading || (is_sys_admin && !selected_customer)}
        size="large"
      >
        {is_downloading ? "Wird heruntergeladen..." : "Kataloge herunterladen"}
      </Button>
    </Box>
  );
};

export const CatalogDownloadCard = ({ layout = "card", ...props }) => {
  const body = <CatalogDownloadBody {...props} />;

  if (layout === "card") {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>{body}</CardContent>
      </Card>
    );
  }

  return <Box sx={{ mb: 3 }}>{body}</Box>;
};

const status_options = [
  { value: "all", label: "Alle Status" },
  { value: "uploaded", label: "Hochgeladen" },
  { value: "previewed", label: "Vorschau erstellt" },
  { value: "processing", label: "Wird verarbeitet" },
  { value: "completed", label: "Abgeschlossen" },
  { value: "failed", label: "Fehlgeschlagen" },
  { value: "cancelled", label: "Abgebrochen" },
  { value: "rollback_processing", label: "Rollback wird verarbeitet" },
  { value: "rolled_back", label: "Zurückgerollt" },
  { value: "rollback_failed", label: "Rollback fehlgeschlagen" },
];

const status_color_map = {
  uploaded: "default",
  previewed: "info",
  processing: "warning",
  completed: "success",
  failed: "error",
  cancelled: "default",
  rollback_processing: "warning",
  rollback_failed: "error",
  rolled_back: "success",
};

const format_datetime = (value) => {
  if (!value) {
    return "–";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
};

const format_stats = (stats) => {
  if (!stats || typeof stats !== "object") {
    return "–";
  }

  if (
    Object.prototype.hasOwnProperty.call(stats, "adds") ||
    Object.prototype.hasOwnProperty.call(stats, "updates") ||
    Object.prototype.hasOwnProperty.call(stats, "removes")
  ) {
    const adds = stats.adds ?? 0;
    const updates = stats.updates ?? 0;
    const removes = stats.removes ?? 0;

    return `+${adds} / ~${updates} / -${removes}`;
  }

  if (
    Object.prototype.hasOwnProperty.call(stats, "restored_user_vertrag") ||
    Object.prototype.hasOwnProperty.call(stats, "restored_user_objects") ||
    Object.prototype.hasOwnProperty.call(stats, "deleted_user_vertrag") ||
    Object.prototype.hasOwnProperty.call(stats, "deleted_user_objects")
  ) {
    const restoredVertrag = stats.restored_user_vertrag ?? 0;
    const restoredObjects = stats.restored_user_objects ?? 0;
    const deletedVertrag = stats.deleted_user_vertrag ?? 0;
    const deletedObjects = stats.deleted_user_objects ?? 0;

    return `↩︎ UV ${restoredVertrag} · ↩︎ Obj ${restoredObjects} · ✖︎ UV ${deletedVertrag} · ✖︎ Obj ${deletedObjects}`;
  }

  return (
    Object.entries(stats)
      .map(([key, value]) => `${key}: ${value}`)
      .join(" · ") || "–"
  );
};

export const JobHistoryCard = ({
  effective_customer_id,
  job_list,
  job_list_loading,
  job_list_error,
  job_status_filter,
  on_status_filter_change,
  on_refresh_jobs,
  job_list_total,
  job_list_page,
  job_list_per_page,
  on_change_page,
  on_change_rows_per_page,
  on_job_preview,
  on_job_apply,
  on_rollback_preview,
  on_rollback_apply,
  is_job_action_running,
}) => {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 2,
          }}
        >
          <Typography variant="h6">Importe</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <FormLabel component="legend" sx={{ mb: 0.5 }}>
                Status filtern
              </FormLabel>
              <Select
                value={job_status_filter}
                onChange={on_status_filter_change}
                size="small"
              >
                {status_options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Liste aktualisieren">
              <span>
                <IconButton
                  onClick={on_refresh_jobs}
                  disabled={job_list_loading || !effective_customer_id}
                >
                  {job_list_loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Refresh />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        {!effective_customer_id && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Bitte wählen Sie zuerst einen Kunden aus, um Import-Jobs anzuzeigen.
          </Alert>
        )}

        {job_list_error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {job_list_error}
          </Alert>
        )}

        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Datei</TableCell>
                <TableCell>Statistik (+ / ~ / -)</TableCell>
                <TableCell>Vorschau</TableCell>
                <TableCell>Importiert am</TableCell>
                <TableCell>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {job_list_loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Stack direction="row" justifyContent="center" spacing={2}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" color="text.secondary">
                        Lade Import-Jobs...
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}

              {!job_list_loading && job_list.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Keine Import-Jobs vorhanden.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {job_list.map((job) => {
                const status_chip_color =
                  status_color_map[job.status] || "default";
                const preview_by_current_user =
                  job.actions?.preview_user_matches;
                const can_preview = job.actions?.can_preview;
                const can_apply = job.actions?.can_apply;
                const can_rollback_preview = job.actions?.can_rollback_preview;
                const can_rollback_apply = job.actions?.can_rollback_apply;

                return (
                  <TableRow key={job.import_job_id} hover>
                    <TableCell>{job.import_job_id}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={job.status_label || job.status}
                          color={status_chip_color}
                          size="small"
                        />
                        {job.actions?.preview_recent === false &&
                          preview_by_current_user && (
                            <Tooltip title="Vorschau ist älter als 30 Minuten">
                              <Typography variant="caption" color="error">
                                TTL abgelaufen
                              </Typography>
                            </Tooltip>
                          )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {job.original_filename}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {job.file_type?.toUpperCase?.() || job.file_type}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format_stats(job.stats)}
                      </Typography>
                      {job.actions?.rollback?.stats && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Rollback: {format_stats(job.actions.rollback.stats)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {job.previewed_at
                          ? format_datetime(job.previewed_at)
                          : "–"}
                      </Typography>
                      {job.imported_by?.name && (
                        <Typography variant="caption" color="text.secondary">
                          von {job.imported_by.name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{format_datetime(job.imported_at)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {can_preview && (
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={is_job_action_running(
                              job.import_job_id,
                              "preview"
                            )}
                            onClick={() => on_job_preview(job)}
                          >
                            {is_job_action_running(
                              job.import_job_id,
                              "preview"
                            ) ? (
                              <CircularProgress size={16} />
                            ) : (
                              "Vorschau"
                            )}
                          </Button>
                        )}

                        {can_apply && (
                          <Button
                            variant="contained"
                            size="small"
                            disabled={is_job_action_running(
                              job.import_job_id,
                              "apply"
                            )}
                            onClick={() => on_job_apply(job)}
                          >
                            {is_job_action_running(
                              job.import_job_id,
                              "apply"
                            ) ? (
                              <CircularProgress
                                size={16}
                                sx={{ color: "white" }}
                              />
                            ) : (
                              "Anwenden"
                            )}
                          </Button>
                        )}

                        {can_rollback_preview && (
                          <Tooltip title="Zurücksetzen-Vorschau anzeigen">
                            <Button
                              variant="outlined"
                              size="small"
                              color="secondary"
                              disabled={is_job_action_running(
                                job.import_job_id,
                                "rollback_preview"
                              )}
                              onClick={() => on_rollback_preview(job)}
                            >
                              {is_job_action_running(
                                job.import_job_id,
                                "rollback_preview"
                              ) ? (
                                <CircularProgress size={16} />
                              ) : (
                                "Zurücksetzen-Vorschau"
                              )}
                            </Button>
                          </Tooltip>
                        )}

                        {can_rollback_apply && (
                          <Tooltip title="Import zurücksetzen">
                            <Button
                              variant="contained"
                              size="small"
                              color="secondary"
                              disabled={is_job_action_running(
                                job.import_job_id,
                                "rollback_apply"
                              )}
                              onClick={() => on_rollback_apply(job)}
                            >
                              {is_job_action_running(
                                job.import_job_id,
                                "rollback_apply"
                              ) ? (
                                <CircularProgress
                                  size={16}
                                  sx={{ color: "white" }}
                                />
                              ) : (
                                "Zurücksetzen"
                              )}
                            </Button>
                          </Tooltip>
                        )}

                        {!can_preview &&
                          !can_apply &&
                          !can_rollback_preview &&
                          !can_rollback_apply && (
                            <Typography variant="body2" color="text.secondary">
                              Keine Aktionen verfügbar
                            </Typography>
                          )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={job_list_total}
          page={job_list_page}
          onPageChange={on_change_page}
          rowsPerPage={job_list_per_page}
          onRowsPerPageChange={on_change_rows_per_page}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Einträge pro Seite"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} von ${count !== -1 ? count : `mehr als ${to}`}`
          }
        />
      </CardContent>
    </Card>
  );
};

export const ImportUploadCard = ({
  is_active,
  import_error,
  upload_step,
  selected_file,
  handle_file_change,
  handle_upload_import,
  is_uploading,
  is_sys_admin,
  selected_customer,
  handle_preview_import,
  is_previewing,
  handle_cancel,
}) => {
  if (!is_active) {
    return null;
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Import durchführen
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Laden Sie eine ausgefüllte Vorlage hoch (CSV oder XLSX). Nutzen Sie
          danach die Vorschau und wenden Sie die Änderungen an.
        </Typography>

        {import_error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {import_error}
          </Alert>
        )}

        {upload_step === "initial" && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<Upload />}
              >
                Datei auswählen
                <input
                  type="file"
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  hidden
                  onChange={handle_file_change}
                />
              </Button>
              <Typography variant="body2" color="text.secondary">
                {selected_file ? selected_file.name : "Keine Datei ausgewählt"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                onClick={handle_upload_import}
                disabled={
                  !selected_file ||
                  is_uploading ||
                  (is_sys_admin && !selected_customer)
                }
                size="large"
              >
                {is_uploading ? <CircularProgress size={20} /> : null}
                {is_uploading ? " Wird hochgeladen..." : "Datei hochladen"}
              </Button>
              <Button
                variant="outlined"
                onClick={handle_cancel}
                disabled={is_uploading}
                size="large"
              >
                Abbrechen
              </Button>
            </Box>
          </Box>
        )}

        {upload_step === "uploaded" && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Datei erfolgreich hochgeladen: {selected_file?.name}
            </Alert>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                onClick={handle_preview_import}
                disabled={is_previewing}
                size="large"
              >
                {is_previewing ? <CircularProgress size={20} /> : null}
                {is_previewing
                  ? " Vorschau wird erstellt..."
                  : "Import-Vorschau generieren"}
              </Button>
              <Button
                variant="outlined"
                onClick={handle_cancel}
                disabled={is_previewing}
                size="large"
              >
                Abbrechen
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export const ImportPreviewCard = ({
  title,
  intro,
  import_error,
  preview_result,
  handle_apply,
  is_applying,
  handle_cancel,
  apply_button_label,
  cancel_button_label,
  apply_message,
  is_polling,
  import_step,
  on_finish_completed,
  kunde_id_ov,
  import_job_id,
}) => {
  const should_render_preview = import_step === "preview" && preview_result;
  const should_render_completed = import_step === "completed";
  const should_render_feedback =
    (apply_message || is_polling) && !should_render_completed;

  if (
    !should_render_preview &&
    !should_render_completed &&
    !import_error &&
    !should_render_feedback
  ) {
    return null;
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        {title && (
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
        )}

        {intro && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {intro}
          </Typography>
        )}

        {import_error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {import_error}
          </Alert>
        )}

        {should_render_preview && (
          <Box>
            <Box sx={{ mb: 3 }}>
              <AssignmentImportPreview
                previewResult={preview_result}
                kunde_id_ov={kunde_id_ov}
                import_job_id={import_job_id}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Button
                variant="contained"
                color="success"
                onClick={handle_apply}
                disabled={!preview_result?.can_apply || is_applying}
                size="large"
              >
                {is_applying ? <CircularProgress size={20} /> : null}
                {is_applying ? " Wird angewendet..." : apply_button_label}
              </Button>

              <Button
                variant="outlined"
                onClick={handle_cancel}
                disabled={is_applying}
                size="large"
              >
                {cancel_button_label}
              </Button>
            </Box>
          </Box>
        )}

        {should_render_completed && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Import abgeschlossen.
            </Alert>

            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                color="success"
                onClick={on_finish_completed}
              >
                Fertig
              </Button>
              <Button variant="outlined" onClick={handle_cancel} size="large">
                Neuen Import starten
              </Button>
            </Stack>
          </Box>
        )}

        {should_render_feedback && (
          <Box sx={{ mt: 2 }}>
            {apply_message && (
              <Alert severity="info" sx={{ mb: 1 }}>
                {apply_message}
              </Alert>
            )}
            {is_polling && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">
                  Status wird abgefragt...
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
